<template>
  <div class="container bigger-top-margin">
    <div class="row justify-content-center">
      <div class="col">
        <div class="card card-default">
          <div class="card-header">How to handle expenses</div>

          <div class="card-body">
            Let’s set the scene for this little corner, just to make sure we’ll be speaking the same language throughout
            all the actions detailed below.<br><br>
            In terms of monetary amounts, we’ll be using the following 3 terms:
            <ul>
              <li><b>Estimation</b> — this is a well-thought (because you’re an awesome planner!) amount of money that you guess or you simply know for sure that you’ll be needing for a certain something</li>
              <li><b>Payment</b> — the amount you actually paid for that certain something… which hopefully is less than or equal to the Estimation</li>
              <li><b>Expense</b> — depending on the case, we’ll be referring with this term to any Estimation, Payment, or Estimation+Payment. It will make sense later on, I promise! :-)</li>
            </ul>
            Since we’re defining terms, let’s cover one more: <b>Not day-specific</b>. The name is pretty much self-explanatory, but let’s verbalise it anyways, just for the sake of it. As you probably guessed, it means that
            you can have expenses not linked to a particular day, but generically allocated to a given month instead. It’s a nice & simple feature, but none of the other budget tracking apps I’ve tried so far had this — and not
            sure about you, but I for one have no way of knowing the exact day I’m gonna go groceries shopping next month or enjoy a dinner out with some friends.<br>
            <br>
            Alright, now that we’re all set with the basics, let’s do some planning, shall we?!<br><br>
            <div class="row">
              <div class="col-md-6">
                <h5>Add expense</h5>
                Once on a ledger’s page, it's hard to miss the yellow square with that nice plus sign <i aria-hidden="true" class="pe pe-plus-circle"/> on the bottom-right corner. It’s time to click on it and add your very first expense!<br>
                <br>
                If you want to plan for a future spending, then set it as an
                Estimate. If you just happened to run into something you couldn’t resist buying, or an unexpected event occurred, then Payment it is. Lastly, if you wanna plan a budget for a certain category (like Food or Education), and you already paid a chunk of it, you can choose Estimate+Payment — set the total estimated budget in the Estimate field, and the partial payout in the Payment field, using integer numbers only.<br>
                <br>
                (Keep in mind that the app will automatically round up values to the nearest integer, if necessary. Thus: 12.5 will be saved as 13.)<br>
                <br>
                Give a name to this expense in the Label field. Decide whether it will be day-specific or not — depending the on the case, the Date field will either store the exact specific day in the DD/MM/YYYY format, or the desired month.<br>
                <br>
                And assess if it’s gonna be a recurring expense or just a
                one-timer — if you check the “Is recurring” box, you’ll see
                3 new fields appearing by magic. It’s not voodoo, it’s just
                skilful programming… kidding, it’s quite simple to do!
                Anyways, back to our expense recurrence setup: establish
                the frequency interval, the frequency unit of time (week,
                month, year) and whether it’s bound to end at a certain
                point (on a given date, or after X occurrences).<br>
                <br>
                And this concludes probably the longest tour of a section on this app.<br><br>
              </div>
              <div class="col-md-6">
                <b-img right rounded thumbnail fluid src="/img/support/add-expense.png" class="mb-2"/>
                <b-img right rounded thumbnail fluid src="/img/support/recurring-expense.png" class="mb-5"/>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <h5>Edit/remove expense</h5>
                Making updates to a given expense is not only possible, but very easy to do.
                Just click on the expense name (from the Ledger page), and you’ll be shown 3 tabs to choose from:
                <ul>
                  <li><b>Details</b> — where you can edit the expense title, or the date (for the non-recurring expenses)</li>
                  <li><b>Estimate</b> — you can change the initial estimation. For recurring expenses, you can either update the current expense, or the future ones as well</li>
                  <li><b>Remove</b> — or you can delete expenses: either the current one, future ones, or past ones included</li>
                </ul>
                3-in-1 never sounded so easy!<br><br>
              </div>
              <div class="col-md-6">
                <b-img right rounded thumbnail fluid src="/img/support/edit-expense.png" class="mb-2"/>
              </div>
            </div>
            <br>
            <div class="row">
              <div class="col-md-6">
                <h5>Add payment</h5>
                You were thinking about grouping multiple payments under the same umbrella? — like “Groceries” or “Utilities”. I thought you might!<br>
                <br>
                That’s why, once you have an expense on your ledger, you can add subsequent payments to it. Just click on the <i aria-hidden="true" class="pe pe-money"/> icon, and specify the amount and description for that particular payment. Once added, it will appear on this same Payments pop-up, together with any other subpayment you’ll be adding for this given expense.<br>
                <br>
                Isn’t that all beautiful and organised?!<br><br>
              </div>
              <div class="col-md-6">
                <b-img right rounded thumbnail fluid src="/img/support/add-payment.png" class="mb-2"/>
              </div>
            </div>
            <br>
            <div class="row">
              <div class="col-md-6">
                <h5>Remove payment</h5>
                You added a payment by mistake, or you changed your mind about an existent one? Use the ‘recycle bin’ <i aria-hidden="true" class="pe pe-trash-o"/> icon next to that payment, and confirm its removal.<br>
                <br>
                Just like that, it’s gone forever!<br><br>
              </div>
              <div class="col-md-6">
                <b-img right rounded thumbnail fluid src="/img/support/remove-payment.png" class="mb-2"/>
              </div>
            </div>
            <br>
            <br>
          </div>
          <div class="floating-bottom-container"><button onclick="window.history.back()" class="btn btn-primary floating-bottom-right">Go Back</button></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Expenses',
  data () {
    return {}
  }
}
</script>
